import React, { useState, useEffect, useMemo } from 'react'
import { 
  Crown, 
  Search, 
  TrendingUp,
  Medal,
  Flame,
  ChevronLeft,
  ChevronRight,
  Trophy,
  Users
} from 'lucide-react'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Card, CardHeader, CardTitle, CardBody } from '@/components/ui/Card'
import { PageLoader } from '@/components/ui/Loader'
import { useLeaderboardStore } from '@/stores/leaderboardStore'
import { useLeaguesStore } from '@/stores/leaguesStore'
import { HeatMeter } from '@/components/gaming/HeatMeter'
import { TeamFlairBadge } from '@/components/ui/TeamFlairBadge'
import { UserProfileModal } from '@/components/ui/UserProfileModal'

interface LeaderboardUser {
  id: string
  rank: number
  username: string
  avatar?: string
  favoriteTeamId?: string | null
  totalPoints: number
  correctPicks: number
  upsetPicks: number
  maxStreak: number
  heatLevel: number
  isCurrentUser?: boolean
  isFriend?: boolean
}

type SortField = 'rank' | 'totalPoints' | 'correctPicks' | 'upsetPicks' | 'maxStreak' | 'heatLevel'
type LeaderboardTab = 'global' | 'friends' | 'league'

// Mock data
const LeaderboardPage: React.FC = () => {
  const { data, isLoading, fetch } = useLeaderboardStore()
  const { leagues, fetchLeagues } = useLeaguesStore()

  const [activeTab, setActiveTab] = useState<LeaderboardTab>('global')
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<SortField>('rank')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedLeagueId, setSelectedLeagueId] = useState<string | null>(null)
  const [viewProfileId, setViewProfileId] = useState<string | null>(null)

  const itemsPerPage = 20

  const apiSort: 'rank' | 'points' | 'accuracy' =
    sortField === 'rank' ? 'rank' : sortField === 'totalPoints' ? 'points' : 'accuracy'

  useEffect(() => {
    // preload leagues for league leaderboard selection
    fetchLeagues()
  }, [fetchLeagues])

  // Pick default league for league leaderboard
  useEffect(() => {
    if (activeTab !== 'league') return
    if (selectedLeagueId) return
    if (leagues.length > 0) {
      setSelectedLeagueId(leagues[0].id)
    }
  }, [activeTab, selectedLeagueId, leagues])

  // Fetch on tab/sort/page
  useEffect(() => {
    const scope = activeTab
    const leagueId = activeTab === 'league' ? selectedLeagueId ?? undefined : undefined

    // Don’t fetch league leaderboard until a league is selected
    if (activeTab === 'league' && !leagueId) return

    fetch({
      scope: scope === 'league' ? 'league' : (scope as any),
      leagueId,
      sort: apiSort,
      limit: itemsPerPage,
      offset: (currentPage - 1) * itemsPerPage,
    })
  }, [activeTab, selectedLeagueId, apiSort, currentPage, fetch, itemsPerPage])

  // Map API rows to component's LeaderboardUser shape
  const users: LeaderboardUser[] = useMemo(() => {
    if (!data?.rows) return []
    return data.rows.map((row: any) => ({
      id: row.userId,
      rank: row.rank,
      username: row.displayName || row.username || 'Unknown',
      avatar: row.avatarUrl || `https://api.dicebear.com/7.x/avataaars/svg?seed=${row.userId}`,
      favoriteTeamId: row.favoriteTeamId,
      totalPoints: row.score,
      correctPicks: row.correctPicks,
      upsetPicks: row.upsets ?? 0,
      maxStreak: row.streak ?? 0,
      heatLevel: Math.min(100, Math.round(row.accuracy ?? 0)),
      isFriend: false, // TODO: wire friends
      isCurrentUser: row.isMe,
    }))
  }, [data?.rows])

  // Filter and sort users (server already sorts, but client search still works)
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users.filter(user => {
      const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase())
      return matchesSearch
    })

    return filtered
  }, [users, searchTerm])

  // Pagination (server handles pagination, but keep client-side for search filtering)
  const totalPages = Math.ceil((data?.meta?.total ?? filteredAndSortedUsers.length) / itemsPerPage)
  const paginatedUsers = filteredAndSortedUsers

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="h-5 w-5 text-yellow-400" />
    if (rank === 2) return <Medal className="h-5 w-5 text-gray-300" />
    if (rank === 3) return <Medal className="h-5 w-5 text-amber-600" />
    return null
  }

  if (isLoading) {
    return <PageLoader text="Loading leaderboard..." />
  }

  return (
          <div className="min-h-screen bg-transparent px-4 py-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div
          >
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg">
                  <Crown className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl lg:text-4xl font-bold text-white">
                    Leaderboard
                  </h1>
                  <p className="text-slate-400 text-lg">
                    See how you stack up against the competition
                  </p>
                </div>
              </div>
              
              {/* Current user highlight if not in top 10 */}
              {(users.find(u => u.isCurrentUser)?.rank ?? 0) > 10 && (
                <Card variant="gaming" className="mb-6 bg-white/[0.05] border-white/10 backdrop-blur-md">
                  <CardBody className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" size="lg">Your Position</Badge>
                        <span className="text-2xl font-bold text-orange-400">
                          #{users.find(u => u.isCurrentUser)?.rank}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-white">
                          {users.find(u => u.isCurrentUser)?.totalPoints.toLocaleString()} points
                        </p>
                        <p className="text-sm text-slate-400">
                          {users.find(u => u.isCurrentUser)?.correctPicks} correct picks
                        </p>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              )}
            </div>

            {/* Context Selection Tabs */}
            <div className="flex justify-center mb-8">
              <div className="bg-white/5 border border-white/10 p-1 rounded-lg inline-flex backdrop-blur-md">
                <Button
                  variant={activeTab === 'global' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab('global')}
                  className="mx-1"
                >
                  Global
                </Button>
                <Button
                  variant={activeTab === 'friends' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab('friends')}
                  className="mx-1"
                >
                  Friends
                </Button>
                <Button
                  variant={activeTab === 'league' ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab('league')}
                  className="mx-1"
                >
                  Leagues
                </Button>
              </div>
            </div>

            <div className="space-y-6">
            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="flex-1">
                <Input
                  placeholder="Search players..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  leftIcon={<Search className="h-4 w-4" />}
                />
              </div>

              {activeTab === 'league' && (
                <div className="flex gap-2">
                  <select
                    value={selectedLeagueId ?? ''}
                    onChange={(e) => setSelectedLeagueId(e.target.value)}
                    className="px-4 py-2 bg-white/5 border border-white/10 backdrop-blur-sm rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 min-w-[220px]"
                  >
                    {leagues.length === 0 ? (
                      <option value="">No leagues joined</option>
                    ) : (
                      leagues.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              )}
              
              <div className="flex gap-2">
                <select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value as SortField)}
                  className="px-4 py-2 bg-white/5 border border-white/10 backdrop-blur-sm rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 min-w-[140px]"
                >
                  <option value="rank">Sort by Rank</option>
                  <option value="totalPoints">Sort by Points</option>
                  <option value="correctPicks">Sort by Correct</option>
                  <option value="upsetPicks">Sort by Upsets</option>
                  <option value="maxStreak">Sort by Streak</option>
                  <option value="heatLevel">Sort by Heat</option>
                </select>
              </div>
            </div>

            {/* Leaderboard Table */}
            <Card variant="elevated" className="bg-white/[0.05] border-white/10 backdrop-blur-md">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-orange-400" />
                    {activeTab === 'global' && 'Global Rankings'}
                    {activeTab === 'friends' && 'Friends Rankings'}
                    {activeTab === 'league' && 'League Rankings'}
                  </CardTitle>
                  <Badge variant="outline" size="sm">
                    {filteredAndSortedUsers.length} players
                  </Badge>
                </div>
              </CardHeader>
                
                <CardBody className="p-0">
                  {/* Desktop Table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-700">
                          <th className="text-left p-4 text-slate-400 font-medium text-sm">Rank</th>
                          <th className="text-left p-4 text-slate-400 font-medium text-sm">Player</th>
                          <th className="text-right p-4 text-slate-400 font-medium text-sm">Points</th>
                          <th className="text-right p-4 text-slate-400 font-medium text-sm">Correct</th>
                          <th className="text-right p-4 text-slate-400 font-medium text-sm">Upsets</th>
                          <th className="text-right p-4 text-slate-400 font-medium text-sm">Streak</th>
                          <th className="text-center p-4 text-slate-400 font-medium text-sm">Heat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedUsers.map((user) => (
                          <tr
                            key={user.id}
                            onClick={() => setViewProfileId(user.id)}
                            className={`
                              border-b border-slate-700 last:border-b-0 hover:bg-slate-700/50 transition-all duration-200 cursor-pointer
                              ${user.isCurrentUser ? 'bg-orange-500/10 border-orange-500/30 shadow-glow' : ''}
                            `}
                          >
                            <td className="p-4">
                              <div className="flex items-center gap-2">
                                {getRankIcon(user.rank)}
                                <span className={`font-bold text-lg ${
                                  user.rank <= 3 ? 'text-orange-400' : 'text-white'
                                }`}>
                                  #{user.rank}
                                </span>
                                {user.rank <= 10 && (
                                  <div className="flex flex-col text-xs">
                                    <TrendingUp className="h-3 w-3 text-green-400" />
                                  </div>
                                )}
                              </div>
                            </td>
                            
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <Avatar
                                  src={user.avatar}
                                  alt={user.username}
                                  fallback={user.username}
                                  size="md"
                                />
                                <div>
                                  <div className="flex items-center gap-2">
                                    <TeamFlairBadge teamId={user.favoriteTeamId} size="xs" />
                                    <span className="font-semibold text-white">
                                      {user.username}
                                    </span>
                                    {user.isCurrentUser && (
                                      <Badge size="sm" variant="primary">
                                        You
                                      </Badge>
                                    )}
                                    {user.isFriend && (
                                      <Badge size="sm" variant="outline">
                                        <Users className="h-3 w-3 mr-1" />
                                        Friend
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            
                            <td className="p-4 text-right">
                              <span className="font-bold text-lg text-orange-400">
                                {user.totalPoints.toLocaleString()}
                              </span>
                            </td>
                            
                            <td className="p-4 text-right">
                              <span className="text-green-400 font-semibold">
                                {user.correctPicks}
                              </span>
                            </td>
                            
                            <td className="p-4 text-right">
                              <span className="text-amber-400 font-semibold">
                                {user.upsetPicks}
                              </span>
                            </td>
                            
                            <td className="p-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Flame className="h-4 w-4 text-red-400" />
                                <span className="text-white font-semibold text-lg">
                                  {user.maxStreak}
                                </span>
                              </div>
                            </td>
                            
                            <td className="p-4">
                              <div className="flex justify-center">
                                <HeatMeter 
                                  mode="compact"
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden space-y-4 p-4">
                    {paginatedUsers.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => setViewProfileId(user.id)}
                        className={`
                          bg-slate-800 rounded-lg p-4 border transition-all duration-200 hover:bg-slate-700/80 cursor-pointer
                          ${user.isCurrentUser 
                            ? 'border-orange-500/50 bg-orange-500/10 shadow-glow' 
                            : 'border-slate-600'
                          }
                        `}
                      >
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            {getRankIcon(user.rank)}
                            <span className="text-xl font-bold text-white">#{user.rank}</span>
                            <Avatar 
                              src={user.avatar} 
                              alt={user.username} 
                              fallback={user.username} 
                              size="md" 
                            />
                            <div>
                              <div className="flex items-center gap-1.5 font-semibold text-white"><TeamFlairBadge teamId={user.favoriteTeamId} size="xs" />{user.username}</div>
                              <div className="flex gap-1 mt-1">
                                {user.isCurrentUser && (
                                  <Badge size="sm" variant="primary">You</Badge>
                                )}
                                {user.isFriend && (
                                  <Badge size="sm" variant="outline">Friend</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-right min-w-[72px]">
                            <div className="text-lg font-bold text-orange-400 tabular-nums leading-tight">
                              {user.totalPoints.toLocaleString()}
                            </div>
                            <div className="text-[11px] text-slate-400 leading-tight">points</div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-4 gap-3 text-center mb-3">
                          <div>
                            <div className="text-green-400 font-bold text-lg">{user.correctPicks}</div>
                            <div className="text-xs text-slate-400">Correct</div>
                          </div>
                          <div>
                            <div className="text-amber-400 font-bold text-lg">{user.upsetPicks}</div>
                            <div className="text-xs text-slate-400">Upsets</div>
                          </div>
                          <div>
                            <div className="text-white font-bold text-lg flex items-center justify-center gap-1">
                              <Flame className="h-4 w-4 text-red-400" />
                              {user.maxStreak}
                            </div>
                            <div className="text-xs text-slate-400">Streak</div>
                          </div>
                          <div>
                            <div className="flex justify-center">
                              <HeatMeter 
                                mode="compact"
                              />
                            </div>
                            <div className="text-xs text-slate-400">Heat</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between p-4 border-t border-slate-700 bg-slate-800/50">
                      <div className="text-sm text-slate-400">
                        Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredAndSortedUsers.length)} of {filteredAndSortedUsers.length} players
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(currentPage - 1)}
                          disabled={currentPage === 1}
                          leftIcon={<ChevronLeft className="h-4 w-4" />}
                        >
                          Previous
                        </Button>
                        
                        <span className="text-sm text-white px-3 py-1 bg-slate-700 rounded">
                          {currentPage} of {totalPages}
                        </span>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCurrentPage(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          rightIcon={<ChevronRight className="h-4 w-4" />}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Empty state for friends/league tabs */}
              {(activeTab === 'friends' || activeTab === 'league') && filteredAndSortedUsers.length === 0 && (
                <div className="text-center py-12">
                  <div className="mx-auto w-24 h-24 bg-slate-800 rounded-full flex items-center justify-center mb-4">
                    {activeTab === 'friends' ? (
                      <Users className="h-8 w-8 text-slate-400" />
                    ) : (
                      <Trophy className="h-8 w-8 text-slate-400" />
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {activeTab === 'friends' ? 'No Friends Yet' : 'No League Selected'}
                  </h3>
                  <p className="text-slate-400 max-w-sm mx-auto">
                    {activeTab === 'friends' 
                      ? 'Connect with friends to see how you compare in friendly competition.'
                      : 'Join a league to see rankings within your group.'
                    }
                  </p>
                  <Button 
                    variant="primary" 
                    className="mt-4"
                    onClick={() => setActiveTab('global')}
                  >
                    View Global Rankings
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      <UserProfileModal userId={viewProfileId} onClose={() => setViewProfileId(null)} />
      </div>
      )
}

export { LeaderboardPage }